// Register.js
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signInWithGoogle, auth, db } from "../firebaseConfig";
import { FcGoogle } from "react-icons/fc";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import logo from "./Asset/logo.png";
import { doc, setDoc, getDoc, addDoc, collection } from "firebase/firestore";

const Register = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { username: claimUsername } = location.state || {};

  const [mode, setMode] = useState("login"); // "login" | "signup" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const predefinedLinks = [
    { name: "Website", image: "https://cdn4.iconfinder.com/data/icons/small-n-flat/24/globe-512.png" },
    { name: "Instagram", image: "https://cdn2.iconfinder.com/data/icons/social-icons-33/128/Instagram-512.png" },
    { name: "Facebook", image: "https://cdn1.iconfinder.com/data/icons/logotypes/32/square-facebook-512.png" },
    { name: "WhatsApp", image: "https://cdn2.iconfinder.com/data/icons/social-media-applications/64/social_media_applications_23-whatsapp-512.png" },
    { name: "TikTok", image: "https://cdn0.iconfinder.com/data/icons/logos-brands-7/512/TikTok_logo_app0-512.png" },
    { name: "Youtube", image: "https://cdn4.iconfinder.com/data/icons/logos-and-brands/512/395_Youtube_logo-512.png" },
    { name: "LinkedIn", image: "https://cdn2.iconfinder.com/data/icons/social-media-2285/512/1_Linkedin_unofficial_colored_svg-512.png" },
    { name: "X", image: "https://freepnglogo.com/images/all_img/1725374683twitter-x-logo.png" },
  ];

  const createUserInFirestore = async (uid, emailVal, name, usernameVal = "") => {
    const userRef = doc(db, "userss", uid);
    await setDoc(userRef, {
      email: emailVal,
      name: name || "",
      uid: uid,
      createdAt: new Date(),
      username: usernameVal || "",
      balance: 0,
    });
    if (usernameVal) {
      await setDoc(doc(db, "usedUsernames", usernameVal), {
        uid,
        timestamp: new Date(),
      });
    }
    const linksRef = collection(db, "userss", uid, "Links");
    for (const link of predefinedLinks) {
      await addDoc(linksRef, { name: link.name, url: "", image: link.image, createdAt: new Date() });
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await auth.signInWithEmailAndPassword(email, password);
      navigate("/profile");
      window.scrollTo(0, 0);
    } catch (err) {
      if (err.code === "auth/user-not-found") setError("No account found with this email.");
      else if (err.code === "auth/wrong-password") setError("Incorrect password.");
      else if (err.code === "auth/invalid-email") setError("Invalid email address.");
      else setError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim()) {
      setError("Please choose a username.");
      return;
    }
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(username.trim())) {
      setError("Username must be 3–20 characters (letters, numbers, underscore only).");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const trimmedUsername = username.trim().toLowerCase();
      const usedUsernameDoc = await getDoc(doc(db, "usedUsernames", trimmedUsername));
      if (usedUsernameDoc.exists()) {
        setError("Username is already taken. Please choose a different one.");
        setLoading(false);
        return;
      }

      const result = await auth.createUserWithEmailAndPassword(email, password);
      const { uid } = result.user;
      await result.user.updateProfile({ displayName: displayName });
      const userRef = doc(db, "userss", uid);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        await createUserInFirestore(uid, email, displayName, trimmedUsername);
      }
      navigate("/profile");
      window.scrollTo(0, 0);
    } catch (err) {
      if (err.code === "auth/email-already-in-use") setError("An account with this email already exists. Please log in.");
      else if (err.code === "auth/invalid-email") setError("Invalid email address.");
      else if (err.code === "auth/weak-password") setError("Password is too weak.");
      else setError("Sign up failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!email) { setError("Please enter your email address."); return; }
    setLoading(true);
    try {
      await auth.sendPasswordResetEmail(email);
      setSuccess("Password reset email sent! Check your inbox.");
    } catch (err) {
      if (err.code === "auth/user-not-found") setError("No account found with this email.");
      else if (err.code === "auth/invalid-email") setError("Invalid email address.");
      else setError("Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);
    try {
      const user = await signInWithGoogle();
      if (user) {
        const { email: gEmail, uid, displayName: gName } = user;
        const userRef = doc(db, "userss", uid);
        const userDoc = await getDoc(userRef);
        if (!userDoc.exists()) {
          await createUserInFirestore(uid, gEmail, gName);
        }
        navigate("/profile");
        window.scrollTo(0, 0);
      }
    } catch (err) {
      setError("Google sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-gradient-to-r from-black to-amber-900 dark:text-white px-4 py-10">
      <img src={logo} className="w-28 mb-6 mt-14 rounded-full" alt="VETH INTERIORS Logo" />

      {claimUsername && (
        <h2 className="mt-2 mb-4 text-2xl font-bold text-center">
          Claim <b>@{claimUsername}</b> today
        </h2>
      )}

      {/* Tab Toggle — hidden on forgot screen */}
      {mode !== "forgot" && (
        <div className="flex rounded-full bg-gray-100 dark:bg-gray-800 p-1 mb-6 w-full max-w-xs">
          <button
            onClick={() => { setMode("login"); setError(""); setSuccess(""); }}
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all ${
              mode === "login"
                ? "bg-gradient-to-r from-amber-600 to-yellow-600 text-white shadow"
                : "text-gray-500 dark:text-gray-400"
            }`}
          >
            Log In
          </button>
          <button
            onClick={() => { setMode("signup"); setError(""); setSuccess(""); }}
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all ${
              mode === "signup"
                ? "bg-gradient-to-r from-amber-600 to-yellow-600 text-white shadow"
                : "text-gray-500 dark:text-gray-400"
            }`}
          >
            Create Account
          </button>
        </div>
      )}

      <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700">
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm rounded-lg border border-red-200 dark:border-red-700">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-sm rounded-lg border border-green-200 dark:border-green-700">
            {success}
          </div>
        )}

        {mode === "forgot" ? (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="text-center mb-2">
              <h3 className="font-bold text-gray-900 dark:text-white text-lg">Reset Password</h3>
              <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                Enter your email and we'll send you a reset link.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm outline-none focus:border-amber-500 dark:bg-gray-800 dark:text-white"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-bold rounded-full shadow hover:from-amber-700 hover:to-yellow-700 transition-all disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
            <button
              type="button"
              onClick={() => { setMode("login"); setError(""); setSuccess(""); }}
              className="w-full py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-amber-600 transition-colors"
            >
              ← Back to Login
            </button>
          </form>
        ) : mode === "login" ? (
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm outline-none focus:border-amber-500 dark:bg-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm outline-none focus:border-amber-500 dark:bg-gray-800 dark:text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => { setMode("forgot"); setError(""); setSuccess(""); }}
                className="text-xs text-amber-600 dark:text-amber-400 hover:underline"
              >
                Forgot password?
              </button>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-bold rounded-full shadow hover:from-amber-700 hover:to-yellow-700 transition-all disabled:opacity-60"
            >
              {loading ? "Logging in..." : "Log In"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleEmailSignup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                placeholder="Your full name"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm outline-none focus:border-amber-500 dark:bg-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))}
                required
                placeholder="your_username"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm outline-none focus:border-amber-500 dark:bg-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm outline-none focus:border-amber-500 dark:bg-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Minimum 6 characters"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm outline-none focus:border-amber-500 dark:bg-gray-800 dark:text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirm Password</label>
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Re-enter your password"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm outline-none focus:border-amber-500 dark:bg-gray-800 dark:text-white"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-bold rounded-full shadow hover:from-amber-700 hover:to-yellow-700 transition-all disabled:opacity-60"
            >
              {loading ? "Creating account..." : "Create Account"}
            </button>
          </form>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          <span className="text-xs text-gray-400">or</span>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
        </div>

        {/* Google Sign In */}
        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 py-3 border border-gray-300 dark:border-gray-600 rounded-full text-gray-700 dark:text-gray-300 font-medium text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-all shadow-sm"
        >
          <FcGoogle size={22} />
          Continue with Google
        </button>
      </div>

      <p className="mt-4 text-xs text-gray-400 text-center max-w-xs">
        By continuing, you agree to our Terms of Service. For support: support@vethinteriors.com
      </p>
    </div>
  );
};

export default Register;
